import React, { useState } from "react";
import {
  Drawer,
  Form,
  Input,
  message,
  Button,
  Table,
  Tooltip,
  Typography,
  Row,
  Col,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { CREATE_ZONE } from "./requests";
import { zoneTypes } from "../../../../types";

const { Title } = Typography;

interface CreateZoneProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  refetch: () => void;
}

const CreateZone: React.FC<CreateZoneProps> = ({ showModal, setShowModal, refetch }) => {
  const [form] = Form.useForm();
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
  const [newSubZoneName, setNewSubZoneName] = useState<string>("");
  const [subZones, setSubZones] = useState<typeof zoneTypes.subZone[]>([]);
  const [editingSubZone, setEditingSubZone] = useState<typeof zoneTypes.subZone | null>(null);

  const [createZoneMutation] = useMutation(CREATE_ZONE);

  const handleAddSubZone = () => {
    if (!newSubZoneName) {
      message.error("Por favor ingresa el nombre de la subzona");
      return;
    }
    if (editingSubZone) {
      setSubZones(
        subZones.map((sz) =>
          sz.id === editingSubZone.id ? { ...sz,
            name: newSubZoneName } : sz),
      );
      setEditingSubZone(null);
    } else {
      setSubZones([...subZones, { id: Date.now(),
        name: newSubZoneName }]);
    }
    setNewSubZoneName("");
  };

  const handleEditSubZone = (subZone: typeof zoneTypes.subZone) => {
    setEditingSubZone(subZone);
    setNewSubZoneName(subZone.name);
  };

  const columns = [
    { title: "Nombre",
      dataIndex: "name",
      key: "name",
      width: "75%" },
    {
      title: "Acciones",
      key: "actions",
      width: "25%",
      render: (row: typeof zoneTypes.subZone) => (
        <>
          <Tooltip title="Editar">
            <Button
              type="primary"
              shape="circle"
              style={{ margin: 5 }}
              icon={<EditOutlined />}
              onClick={() => handleEditSubZone(row)}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const onFinish = async (values: { name: string }) => {
    setLoadingCreate(true);
    try {
      const payload = {
        variables: {
          zone: {
            name: values.name,
            subZones: subZones.map((sz) => sz.name),
          },
        },
      };

      await createZoneMutation(payload);
      await refetch();
      message.success("Zona creada exitosamente");
    } catch (err) {
      console.error(err);
      message.error("Hubo un error");
    } finally {
      onCancel();
      setLoadingCreate(false);
    }
  };

  const onCancel = () => {
    setShowModal(false);
    form.resetFields();
    setSubZones([]);
    setNewSubZoneName("");
    setEditingSubZone(null);
  };

  return (
    <Drawer
      open={showModal}
      title="Crear Zona"
      footer={null}
      onClose={onCancel}
      width={"50%"}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="name"
          label="Nombre"
          rules={[{ required: true,
            message: "Por favor ingresa el nombre" }]}
        >
          <Input placeholder="Ingrese el Nombre" />
        </Form.Item>
        <Form.Item>
          <Button
            loading={loadingCreate}
            size="large"
            block
            type="primary"
            htmlType="submit"
          >
            Crear Zona
          </Button>
        </Form.Item>
      </Form>

      <Title level={4}>
        {editingSubZone ? "Editar Subzona" : "Agregar Nueva Subzona"}
      </Title>
      <Row>
        <Col span={20}>
          <Input
            placeholder="Nombre de la Subzona"
            value={newSubZoneName}
            onChange={(e) => setNewSubZoneName(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Button
            type="primary"
            onClick={handleAddSubZone}
            style={{ marginLeft: 10 }}
          >
            {editingSubZone ? "Actualizar" : "Agregar"}
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="id"
        title={() => <Title level={4}>Municipios</Title>}
        dataSource={subZones}
        columns={columns}
        pagination={false}
      />
    </Drawer>
  );
};

export default CreateZone;
